<template>
    <div class="stage-quotation">
        <div class="quotation-disclaimer">
            This cost calculator provides a rough cost estimate and delivery costs are rounded up to the nearest distance and maximum quantity per load. For a full legal quotation, please submit this
            form and one of our friendly representatives will get back to you.
        </div>
        <div class="contractor-info-block" v-if="$store.state.customerType === 'contractor'">
            <div class="contractor-info-title">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path></svg>
                Contractor Quote
            </div>
            <div class="contractor-info-row" v-if="$store.state.contractorDetails.vatNumber">
                <span class="ci-label">VAT Number:</span>
                <span class="ci-value">{{ $store.state.contractorDetails.vatNumber }}</span>
            </div>
            <div class="contractor-info-row" v-if="$store.state.contractorDetails.registrationNumber">
                <span class="ci-label">Registration Number:</span>
                <span class="ci-value">{{ $store.state.contractorDetails.registrationNumber }}</span>
            </div>
            <div class="contractor-info-row">
                <span class="ci-label">VAT Rate Applied:</span>
                <span class="ci-value">{{ $store.getters.vatRate }}%</span>
            </div>
        </div>
        <div class="quotation" v-if="!$store.state.calculatedResults?.exceedsPalletLimit && !$store.state.calculatedResults?.exceedsDistanceLimit">
            <div class="quotation-column">
                <div class="column-title" v-if="!$store.state.isCalculating">Your Products</div>
                <div class="products-list" v-if="!$store.state.isCalculating">
                    <div class="product-list-item" v-for="product in $store.state.calculatedResults.products">
                        <div class="product-list-item-column">
                            <div class="product-category">{{ product.type }}</div>
                            <div class="product-name">
                                {{ product.name }} <span v-if="product.receivingType === 'delivery'">({{ product.palettes }} {{ getPackageDisplayType(product.type_id) }}*)</span>
                            </div>
                        </div>
                        <div class="product-list-item-column fixed">
                            <div class="product-variation">({{ product.variation }})</div>
                        </div>
                        <div class="product-list-item-column fixed">
                            <div class="product-quantity">x{{ product.quantity }} {{ quantityLabelPart(product.quantityType) }}</div>
                        </div>
                        <div class="product-list-item-column fixed">
                            <div class="product-subtotal">R {{ formatCurrency(product.price_excl_vat) }}</div>
                        </div>
                    </div>
                </div>
                <div class="products-list-loading" v-else>
                    <div class="lds-default">
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                        <div></div>
                    </div>
                    <div class="loader-label">Calculating...</div>
                </div>
            </div>
            <div class="quotation-column">
                <div class="results-card" v-if="$store.getters.calculationTotalResults">
                    <div class="column-title">Your Quotation</div>
                    <div class="results-card-row">
                        <div class="label">Receiving Method</div>
                        <div class="value" v-if="!$store.state.isCalculating">{{ $store.getters.calculationTotalResults.receivingType === 'delivery' ? 'Delivery' : 'Collection' }}</div>
                        <div class="value-loading" v-else>Calculating...</div>
                    </div>
                    <div class="results-card-row" v-if="$store.getters.calculationTotalResults.receivingType === 'delivery'">
                        <div class="label">Delivery Distance</div>
                        <div class="value" v-if="!$store.state.isCalculating">{{ formatCurrency($store.getters.calculationTotalResults.distance) }} km</div>
                        <div class="value-loading" v-else>Calculating...</div>
                    </div>
                    <div class="results-card-row" v-if="$store.getters.calculationTotalResults.receivingType === 'delivery'">
                        <div class="label">Total Pallets/Packs</div>
                        <div class="value" v-if="!$store.state.isCalculating">{{ $store.getters.calculationTotalResults.palettes_total }}</div>
                        <div class="value-loading" v-else>Calculating...</div>
                    </div>
                    <div class="results-card-row">
                        <div class="label">{{ $store.getters.calculationTotalResults.discountCode ? 'Subtotal after discount' : 'Subtotal' }}</div>
                        <div class="value" v-if="!$store.state.isCalculating">R {{ formatCurrency($store.getters.calculationTotalResults.products_price_excl_vat_subtotal) }}</div>
                        <div class="value-loading" v-else>Calculating...</div>
                    </div>
                    <div class="results-card-row" v-if="$store.getters.calculationTotalResults.discountCode">
                        <div class="label">Discount ({{ $store.getters.calculationTotalResults.discountPercent }}%)</div>
                        <div class="value" v-if="!$store.state.isCalculating">- R {{ formatCurrency($store.getters.calculationTotalResults.discount_amount_excl_vat) }}</div>
                        <div class="value-loading" v-else>Calculating...</div>
                    </div>
                    <div class="results-card-row" v-if="$store.getters.calculationTotalResults.receivingType === 'delivery'">
                        <div class="label">Shipping</div>
                        <div class="value" v-if="!$store.state.isCalculating">R {{ formatCurrency($store.getters.calculationTotalResults.shipping_excl_vat) }}</div>
                        <div class="value-loading" v-else>Calculating...</div>
                    </div>
                    <div class="results-card-row">
                        <div class="label">VAT ({{ $store.getters.vatRate }}%)</div>
                        <div class="value" v-if="!$store.state.isCalculating">R {{ formatCurrency($store.getters.calculationTotalResults.total_vat) }}</div>
                        <div class="value-loading" v-else>Calculating...</div>
                    </div>
                    <div class="results-card-row em">
                        <div class="label">Total</div>
                        <div class="value" v-if="!$store.state.isCalculating">R {{ formatCurrency($store.getters.calculationTotalResults.total_incl_vat) }}</div>
                        <div class="value-loading" v-else>Calculating...</div>
                    </div>
                </div>
            </div>
        </div>
        <div v-else-if="!$store.state.calculatedResults?.exceedsPalletLimit && $store.state.calculatedResults?.exceedsDistanceLimit" class="exceeding-message">
            Your delivery exceeds our maximum range of 250km for this calculator. For orders with deliveries farther than 250km, kindly contact our office for an accurate estimate -&nbsp;
            <a href="mailto:sales@mvabricks.co.za" target="_blank" rel="nofollow">sales@mvabricks.co.za</a>&nbsp;|&nbsp;<a href="tel:0123860050" target="_blank" rel="nofollow">(012) 386 0050</a>
        </div>
        <div v-else-if="$store.state.calculatedResults?.exceedsPalletLimit && !$store.state.calculatedResults?.exceedsDistanceLimit" class="exceeding-message">
            Your delivery exceeds the maximum pallets for this calculator. For large orders, kindly contact our office for an accurate estimate -&nbsp;
            <a href="mailto:sales@mvabricks.co.za" target="_blank" rel="nofollow">sales@mvabricks.co.za</a>&nbsp;|&nbsp;<a href="tel:0123860050" target="_blank" rel="nofollow">(012) 386 0050</a>
        </div>
    </div>
</template>

<script>
export default {
	name: "stage-quotation",
	methods: {
		quantityLabelPart: (quantityType) => {
			if (quantityType === "single") {
				return "items";
			}
			if (quantityType === "sqm") {
				return "m²";
			}
			if (quantityType === "cubic") {
				return "m³";
			}
			return "N/A";
		},
		formatCurrency: (number) => {
			const parsed = Number.parseFloat(number);
			const fixed = parsed.toFixed(2);
			const formatted = fixed.replace(/\d(?=(\d{3})+\.)/g, "$&,");
			return formatted;
		},
		getPackageDisplayType(type) {
			if (type === "paving" || type === "kerbs") {
				return "pallets";
			}
			return "packs";
		},
	},
};
</script>

<style lang="scss" scoped>
.contractor-info-block {
    display: flex;
    flex-direction: column;
    gap: 6px;
    background-color: transparentize($cl-primary, 0.93);
    border-left: 3px solid $cl-primary;
    padding: 14px 18px;
    margin-bottom: 24px;
    border-radius: 0 4px 4px 0;

    .contractor-info-title {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: $cl-primary;
        margin-bottom: 6px;
    }

    .contractor-info-row {
        display: flex;
        gap: 8px;
        font-size: 14px;
        .ci-label {
            font-weight: 600;
            min-width: 160px;
        }
        .ci-value {
            color: #333;
        }
    }
}

.exceeding-message {
    a {
        color: $cl-primary;
        text-decoration: none;
        opacity: 1;
        transition: opacity 150ms ease-out;
        &:hover {
            opacity: 0.75;
        }
    }
}
.lds-default {
    display: inline-block;
    position: relative;
    width: 80px;
    height: 80px;
    transform: scale(0.7);
}
.lds-default div {
    position: absolute;
    width: 6px;
    height: 6px;
    background: $cl-primary;
    border-radius: 50%;
    animation: lds-default 1.2s linear infinite;
}
.lds-default div:nth-child(1) {
    animation-delay: 0s;
    top: 37px;
    left: 66px;
}
.lds-default div:nth-child(2) {
    animation-delay: -0.1s;
    top: 22px;
    left: 62px;
}
.lds-default div:nth-child(3) {
    animation-delay: -0.2s;
    top: 11px;
    left: 52px;
}
.lds-default div:nth-child(4) {
    animation-delay: -0.3s;
    top: 7px;
    left: 37px;
}
.lds-default div:nth-child(5) {
    animation-delay: -0.4s;
    top: 11px;
    left: 22px;
}
.lds-default div:nth-child(6) {
    animation-delay: -0.5s;
    top: 22px;
    left: 11px;
}
.lds-default div:nth-child(7) {
    animation-delay: -0.6s;
    top: 37px;
    left: 7px;
}
.lds-default div:nth-child(8) {
    animation-delay: -0.7s;
    top: 52px;
    left: 11px;
}
.lds-default div:nth-child(9) {
    animation-delay: -0.8s;
    top: 62px;
    left: 22px;
}
.lds-default div:nth-child(10) {
    animation-delay: -0.9s;
    top: 66px;
    left: 37px;
}
.lds-default div:nth-child(11) {
    animation-delay: -1s;
    top: 62px;
    left: 52px;
}
.lds-default div:nth-child(12) {
    animation-delay: -1.1s;
    top: 52px;
    left: 62px;
}
@keyframes lds-default {
    0%,
    20%,
    80%,
    100% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.8);
    }
}

.stage-headings {
    .quotation-stage-title {
        font-size: 18px;
        font-weight: 600;
        color: $cl-primary;
    }
}

.quotation-disclaimer {
    margin-bottom: 40px;
    font-style: italic;
}

.products-list-loading {
    min-height: 199px;
    display: flex;
    align-items: center;
    justify-content: center;
    .loader-label {
        font-weight: 600;
        font-size: 18px;
        // margin-left: 20px;
        color: $cl-primary;
    }
}

.quotation {
    display: flex;

    @media screen and (max-width: 768px) {
        flex-flow: row wrap;
    }

    .column-title {
        font-weight: 600;
        color: $cl-primary;
        font-size: 18px;
    }

    .products-list {
        max-height: 400px;
        overflow-y: auto;
        padding-right: 10px;

        @media screen and (max-width: 768px) {
            width: 100%;
        }

        /* width */
        &::-webkit-scrollbar {
            width: 8px;
        }

        /* Track */
        &::-webkit-scrollbar-track {
            background: #f1f1f1;
        }

        /* Handle */
        &::-webkit-scrollbar-thumb {
            background: #888;
        }

        /* Handle on hover */
        &::-webkit-scrollbar-thumb:hover {
            background: #555;
        }

        .product-list-item {
            padding: 10px 0;
            display: flex;

            &:not(:last-child) {
                border-bottom: 1px solid #efefef;
            }

            @media only screen and (max-width: 768px) {
                flex-flow: row wrap;
            }

            .product-list-item-column {
                flex-grow: 1;
                &.fixed {
                    flex-grow: 0;
                    width: 120px;
                    text-align: right;

                    @media only screen and (max-width: 768px) {
                        width: 33.33%;
                        text-align: left;
                        margin-top: 4px;
                    }
                }

                @media only screen and (max-width: 768px) {
                    &:first-child {
                        width: 100%;
                    }
                }

                .product-category {
                    font-size: 14px;
                    opacity: 0.6;
                    margin-bottom: 4px;
                }

                .product-name {
                    font-weight: 600;
                }

                .product-variation {
                    opacity: 0.6;
                }                

                .product-subtotal {
                    font-style: italic;
                }
            }
        }
    }

    .quotation-column {
        &:first-child {
            width: 65%;
            box-sizing: border-box;
            padding-right: 20px;
        }

        &:last-child {
            width: 35%;
            box-sizing: border-box;
            padding-left: 20px;
        }

        @media only screen and (max-width: 768px) {
            &:first-child {
                width: 100%;
                padding-right: 0;
            }

            &:last-child {
                width: 100%;
                padding-left: 0;
                padding-top: 20px;
            }
        }

        .results-card {
            background-color: #efefef;
            box-sizing: border-box;
            padding: 20px;

            .results-card-row {
                display: flex;
                justify-content: space-between;
                margin-top: 10px;
                .label {
                    font-weight: 600;
                }

                &.em {
                    border-top: 1px solid #a7a7a7;
                    padding-top: 10px;
                    .value {
                        font-weight: 600;
                        color: $cl-primary;
                    }
                }
            }
        }
    }
}
</style>